import { ArrowLeftIcon, ArrowRightIcon, ShieldPlusIcon, UsersIcon } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'

import { BottomSheet } from 'components/BottomSheet'
import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { UnrecoverableErrorBanner } from 'components/members/components/UnrecoverableErrorBanner'
import { useHideIntercomLauncher } from 'components/reusable/intercom/IntercomLauncher'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { RightDrawer } from 'components/RightDrawer'
import { Button, ButtonGroup, TextLink, useBreakpoint } from 'slab'

import { type StepComponentProps } from '../types'
import { MedicalPlanCard } from './MedicalPlanCard'
import { PageErrorBanner } from './PageErrorBanner'
import { PlanGranularFilters } from './PlanGranularFilters'
import { type MedicalPlanDisplayItem } from '../medicalPlanTypes'
import { useOnboarding } from '../OnboardingContext'
import { useFilteredPlans } from '../pages/MedicalPlansPage'
import { InNetworkPlansCard } from './InNetworkPlansCard/InNetworkPlansCard'
import { InNetworkProviderCard } from './InNetworkProviderCard/InNetworkProviderCard'
import { useProviderInNetworkVerificationContext } from './ProviderInNetworkVerificationContext/ProviderInNetworkVerificationContext'
import { ProviderSearchModal } from './ProviderSearchModal'
import { providerInNetworkSupportLink } from './ProviderSearchV2'
import { getLabeledPlans, showThisRecommendedPlan } from '../utils'
import { useSubmitInNetworkVerificationRequest } from '../utils/useSubmitInNetworkVerificationRequest'
import { MedicalDetails } from './SmartBudget/MedicalDetails'

export function ProviderInNetworkVerificationTakeover ({
  navigateToPreviousStep,
  navigateToSpecificStep,
}: StepComponentProps): React.JSX.Element {
  const { breakpointIsAtLeast } = useBreakpoint()
  const { onboardingData } = useOnboarding()
  const [openedPlan, setOpenedPlan] = useState<MedicalPlanDisplayItem | null>(null)
  const [providerSearchModalOpen, setProviderSearchModalOpen] = useState(false)
  const { submitInNetworkVerificationRequest, isPending, error, confirmationModal } = useSubmitInNetworkVerificationRequest(
    useCallback(() => navigateToSpecificStep?.('medical_plan'), [navigateToSpecificStep])
  )
  const [showReviewPage, setShowReviewPage] = useState(false)
  const { canSubmit, selectedProviderNpis, selectedPlans, onToggleSelectedPlan, onRemovePlan, turnaroundDescription } = useProviderInNetworkVerificationContext()
  useHideIntercomLauncher()

  useEffect(() => {
    window.segment?.track('InNetworkProviderVerification - view takeover')
  }, [])

  const {
    availablePlans,
    totalPlans,
    lowestCostPlan,
    mostComprehensivePlan,
    renewalPlan,
    planFilters,
    filterNames,
    setPlanFilters,
    medicalPlanLoading,
    medicalPlanError,
  } = useFilteredPlans()
  const labeledPlans = getLabeledPlans({
    lowestCostPlan,
    mostComprehensivePlan,
    renewalPlan: renewalPlan || undefined,
  })
  const city = onboardingData?.member?.city

  const setShowMedicalPlanDetail = (plan: MedicalPlanDisplayItem) => {
    setOpenedPlan(plan)
  }

  const openProviderSearchModal = () => setProviderSearchModalOpen(true)

  if (medicalPlanError) {
    return <PageErrorBanner error={medicalPlanError as GrapeAPIErrorType} />
  }

  const closeDrawer = () => setOpenedPlan(null)
  const isIndividual =
    (onboardingData?.additional_participants || []).length === 0

  return (
    <>
      <ProviderSearchModal
        open={providerSearchModalOpen}
        onOpenChange={setProviderSearchModalOpen}
      />
      {confirmationModal}
      <div className='flex fixed inset-0 overflow-x-auto overflow-y-hidden relative isolate'>
        <div className='md:w-[13.125rem]' />
        <main className='overflow-y-auto h-screen md:basis-[36rem] md:shrink-0 grow'>
          <div className='p-4 md:pt-12 md:pb-10 md:pr-10 xl:pr-16 md:pl-6'>
            <div className='flex flex-col md:max-w-[44rem]'>
              {!!error && <UnrecoverableErrorBanner />}
              {
                !breakpointIsAtLeast('xs') && (
                  <div className='place-self-start'>
                    <TextLink onClick={() => showReviewPage ? setShowReviewPage(false) : navigateToPreviousStep?.()}>
                      <span className='flex items-center gap-1 cursor-pointer'>
                        <ArrowLeftIcon size={14} className='ml-1 inline text-gray-400' />
                        Back
                      </span>
                    </TextLink>
                  </div>
                )
              }

              {
                showReviewPage
                  ? (
                    <div className='flex flex-col gap-4 mt-4'>
                      <span className='body-sm text-gray-800'>We'll check whether the following providers are in-network with your selected plans</span>
                      <InNetworkProviderCard openProviderSearchModal={openProviderSearchModal} />
                      <InNetworkPlansCard
                        plans={selectedPlans}
                        onRemovePlan={onRemovePlan}
                        onSelectMorePlansForVerification={() => setShowReviewPage(false)}
                      />
                    </div>
                    )
                  : (
                    <>
                      <span className='heading-md text-main font-semi-bold my-4'>Want us to call your providers?</span>
                      <span className='body-sm text-main mb-4'>
                        We share the most up-to-date info available, but provider networks can change frequently.
                        Since having your providers in-network matters, we can help you verify!
                        {turnaroundDescription}
                        <TextLink href={providerInNetworkSupportLink} size='small'>
                          You can also confirm directly with your provider.
                        </TextLink>
                      </span>

                      <PlanGranularFilters
                        carrierFilterNames={filterNames.carriers}
                        metalTierFilterNames={filterNames.metalTiers}
                        planTypeFilterNames={filterNames.planTypes}
                        filters={planFilters}
                        setFilters={setPlanFilters}
                      />

                      <div className='body mb-6 text-gray-800'>
                        {totalPlans} medical plan{totalPlans === 1 ? '' : 's'}
                        {city ? ` in ${city}` : ''}
                      </div>
                      <div className='flex flex-col gap-4'>
                        {medicalPlanLoading && <LoadingSpinnerCentered />}
                        {labeledPlans
                          .filter(({ plan }) => showThisRecommendedPlan(plan.plan, planFilters))
                          .map(({ plan, planBadgeCategories }) => (
                            <div key={plan.plan.id}>
                              <MedicalPlanCard
                                badgeCategories={planBadgeCategories}
                                planDisplayItem={plan}
                                setShowMedicalPlanDetail={setShowMedicalPlanDetail}
                                onTogglePlanVerification={onToggleSelectedPlan}
                                showInNetworkVerificationCta={false}
                              />
                            </div>
                          ))}
                        {availablePlans.length > 0 &&
                        availablePlans.map((plan) => {
                          return (
                            <MedicalPlanCard
                              key={plan.plan.id}
                              planDisplayItem={plan}
                              setShowMedicalPlanDetail={setShowMedicalPlanDetail}
                              onTogglePlanVerification={onToggleSelectedPlan}
                              showInNetworkVerificationCta={false}
                            />
                          )
                        })}
                      </div>
                    </>
                    )
              }
            </div>
          </div>
        </main>
        {
          breakpointIsAtLeast('xs')
            ? (
              <div className='overflow-y-auto h-screen bg-gray-50 shadow-default relative z-10 shrink-1 min-w-[19rem] w-full max-w-[22.5rem] bg-gradient-to-t from-gray-50 to-thatch-100'>
                <div className='h-full flex flex-col justify-between'>
                  <div className='pt-12 px-6 flex flex-col gap-6 overflow-auto shrink-0 min-h-full'>
                    <div className='body text-main'>
                      We'll check whether the following providers are in-network with your selected plans
                    </div>
                    <InNetworkProviderCard openProviderSearchModal={openProviderSearchModal} />
                    <InNetworkPlansCard
                      plans={selectedPlans}
                      onRemovePlan={onRemovePlan}
                    />
                    <div className='pb-32' />
                  </div>
                  <div className='py-4 px-6 border-t boder-gray-200 fixed bottom-0 w-[22.5rem] bg-gray-50'>
                    <div className='caption text-gray-500 mb-3'>
                      Note: we can only run this once. Double check you have the right plans and providers before submitting!
                    </div>
                    <ButtonGroup>
                      <Button onClick={() => navigateToPreviousStep?.()} disabled={isPending}>Cancel</Button>
                      <Button variant='primary' disabled={isPending || !canSubmit} onClick={submitInNetworkVerificationRequest}>Submit</Button>
                    </ButtonGroup>
                  </div>
                </div>
              </div>
              )
            : (
              <div className='fixed bottom-0 left-0 right-0 '>
                <div className='py-3 px-4 bg-gray-50'>
                  {
                  showReviewPage
                    ? (
                      <div>
                        <div className='caption text-gray-500 mb-3'>
                          Note: we can only run this once. Double check you have the right plans and providers before submitting!
                        </div>
                        <Button size='large' width='full' variant='primary' onClick={submitInNetworkVerificationRequest} disabled={isPending || !canSubmit} rightIcon={ArrowRightIcon}>
                          Submit
                        </Button>
                      </div>
                      )
                    : (
                      <div className='flex items-center justify-between'>
                        <div className='flex flex-col gap-1 body-sm text-gray-800'>
                          <div className='flex items-center gap-1'>
                            <UsersIcon size={14} />
                            <span>{selectedProviderNpis.length} {selectedProviderNpis.length === 1 ? 'provider' : 'providers'} selected</span>
                          </div>
                          <div className='flex items-center gap-1'>
                            <ShieldPlusIcon size={14} />
                            <span>{selectedPlans.length} {selectedPlans.length === 1 ? 'plan' : 'plans'}  to check</span>
                          </div>
                        </div>
                        <Button size='large' variant='primary' onClick={() => setShowReviewPage(true)} rightIcon={ArrowRightIcon}>
                          Review
                        </Button>
                      </div>
                      )
                }
                </div>
                <div
                  className='!absolute pointer-events-none z-[1] top-0 left-0 h-full w-full border-emphasis'
                />
              </div>
              )
        }
      </div>

      {!openedPlan
        ? null
        : breakpointIsAtLeast('lg')
          ? (
            <RightDrawer
              open={!!openedPlan}
              onOpenChange={(open) => {
                if (!open) {
                  closeDrawer()
                }
              }}
              width='xl'
              onClose={closeDrawer}
            >
              <MedicalDetails
                medicalPlan={openedPlan}
                isIndividual={isIndividual}
                navigateToSpecificStep={navigateToSpecificStep}
                showInNetworkVerificationCta={false}
              />
            </RightDrawer>
            )
          : (
            <BottomSheet
              open={!!openedPlan}
              onOpenChange={(open) => {
                if (!open) {
                  closeDrawer()
                }
              }}
              onClose={closeDrawer}
              showCloseButton
            >
              <div className='p-4'>
                <MedicalDetails
                  medicalPlan={openedPlan}
                  isIndividual={isIndividual}
                  navigateToSpecificStep={navigateToSpecificStep}
                  showInNetworkVerificationCta={false}
                />
              </div>
            </BottomSheet>
            )}
    </>
  )
}
